import { Link } from "react-router-dom";
import {
  ArrowRight, Check, X, ShieldCheck, Globe2, Users, Cpu,
  Sparkles, Layers, MapPin,
} from "lucide-react";
import { LANGUAGE_SERVICES, AI_SERVICES, INDUSTRIES, COMPARISON } from "@/data/content";
import Seo from "@/components/Seo";
import HeroCarousel from "@/components/HeroCarousel";
import LangTicker from "@/components/LangTicker";
import Counter from "@/components/Counter";
import PartnersStrip from "@/components/PartnersStrip";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import AccuracyEcosystem from "@/components/AccuracyEcosystem";
import Reveal from "@/components/Reveal";

function Section({ id, dark = false, gray = false, children, className = "" }) {
  return (
    <section
      id={id}
      className={`py-12 lg:py-16 ${dark ? "bg-[#071D49] text-white" : gray ? "bg-[#F7F8FA]" : "bg-white"} ${className}`}
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">{children}</div>
    </section>
  );
}

const WHY = [
  {
    icon: Users,
    title: "Native Human Experts",
    body: "Domain-specialist linguists, not generalists. Every project reviewed by a second native.",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/etfc4dt7_Multinational%20business%20meeting%20with%20flags.jpg",
  },
  {
    icon: Cpu,
    title: "AI-Enhanced Delivery",
    body: "Machine translation + human QA engineered for speed without quality loss.",
  },
  {
    icon: ShieldCheck,
    title: "Enterprise Security",
    body: "Signed NDAs, encrypted file transfer, GDPR-compliant data handling — every engagement.",
  },
  {
    icon: Globe2,
    title: "Global Coverage",
    body: "85+ languages including Caribbean Creole, African, and underserved Asian markets.",
  },
];

const METRICS = [
  { end: 450, suffix: "+", label: "Native linguists" },
  { end: 85, suffix: "+", label: "Languages" },
  { end: 97, suffix: "%", label: "Client retention" },
  { end: 48, suffix: "h", label: "Avg. turnaround" },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        title="Language Intelligence. Global Reach."
        description="Petraloc delivers certified translation, localization, interpretation, and AI-powered language solutions across 85+ languages — built for enterprises."
        path="/"
      />

      {/* ============ HERO CAROUSEL ============ */}
      <HeroCarousel />

      {/* ============ LANGUAGE TICKER ============ */}
      <LangTicker />

      {/* ============ METRICS ============ */}
      <section className="py-12 lg:py-14 bg-white border-b border-[#E5E7EB]" data-testid="metrics-section">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB] rounded-md overflow-hidden">
            {METRICS.map((m, i) => (
              <div key={m.label} className="bg-white p-6 lg:p-7 text-center" data-testid={`metric-${i}`}>
                <p className="numeral text-3xl lg:text-[40px] text-[#071D49]">
                  <Counter end={m.end} suffix={m.suffix} duration={2000 + i * 150} />
                </p>
                <p className="mt-2 text-[12px] tracking-[0.16em] uppercase font-semibold text-[#5B6472]">
                  {m.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY PETRALOC ============ */}
      <Section gray>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <Reveal variant="right" className="lg:col-span-5">
            <p className="eyebrow">Why Petraloc</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
              Built for enterprises that operate everywhere.
            </h2>
            <p className="mt-4 text-[15px] text-[#5B6472] leading-relaxed max-w-md">
              Native specialists, enterprise-grade security, and AI workflows engineered for accuracy at scale.
            </p>
            <div className="mt-6 img-zoom rounded-md overflow-hidden border border-[#E5E7EB] hidden lg:block">
              <img
                src={WHY[0].image}
                alt="Petraloc linguistic team"
                className="w-full h-72 object-cover"
              />
            </div>
          </Reveal>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3.5">
            {WHY.map((c, i) => (
              <Reveal variant="up" delay={i + 1} key={c.title}>
                <div
                  className="ent-card p-6 h-full"
                  data-testid={`why-card-${c.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="w-10 h-10 rounded-md bg-[#071D49] text-white flex items-center justify-center mb-5">
                    <c.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-[#071D49] mb-2">{c.title}</h3>
                  <p className="text-[13px] text-[#5B6472] leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* ============ LANGUAGE vs AI PANELS ============ */}
      <Section>
        <Reveal variant="up" className="text-center max-w-2xl mx-auto mb-10">
          <p className="eyebrow">Two divisions, one standard</p>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
            Language services, enhanced by AI.
          </h2>
          <p className="mt-4 text-[15px] text-[#5B6472] leading-relaxed">
            Every project blends native human expertise with AI-driven quality control.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-5">
          <Reveal variant="left" className="ent-card p-7 lg:p-9 flex flex-col">
            <p className="eyebrow">Language Services</p>
            <h3 className="mt-3 text-2xl font-semibold text-[#071D49]">
              Certified human translation, localization & voice
            </h3>
            <p className="mt-3 text-sm text-[#5B6472] leading-relaxed">
              Native linguists with domain specialization in legal, medical, finance, and technical content — every deliverable reviewed by an independent second pass.
            </p>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] text-[#222222]">
              {LANGUAGE_SERVICES.map((s) => (
                <li key={s.slug} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-1 text-[#1A4E8A] flex-shrink-0" />
                  <span>{s.title}</span>
                </li>
              ))}
            </ul>
            <Link to="/services" data-testid="panel-cta-language" className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1A4E8A] hover:text-[#071D49] link-ink w-fit">
              View Language Services <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Reveal>

          <Reveal variant="right" className="bg-[#071D49] text-white p-7 lg:p-9 rounded-md border border-[#1A4E8A] flex flex-col">
            <p className="eyebrow-teal">AI Language Solutions</p>
            <h3 className="mt-3 text-2xl font-semibold text-white">
              MTPE, evaluation & multilingual data
            </h3>
            <p className="mt-3 text-sm text-white/75 leading-relaxed">
              Native specialists review, validate, and annotate AI-generated content across 85+ languages — keeping LLMs accurate, on-brand, and culturally precise.
            </p>
            <ul className="mt-5 space-y-2 text-[13px] text-white/90">
              {AI_SERVICES.map((s) => (
                <li key={s.slug} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-1 text-[#00D1B2] flex-shrink-0" />
                  <span>{s.shortTitle || s.title}</span>
                </li>
              ))}
            </ul>
            <Link to="/services#ai" data-testid="panel-cta-ai" className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#00D1B2] hover:text-white w-fit">
              View AI Solutions <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Reveal>
        </div>
      </Section>

      {/* ============ RARE & UNDERSERVED LANGUAGES ============ */}
      <Section>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <Reveal variant="right" className="lg:col-span-6">
            <p className="eyebrow-gold">Caribbean · African · Low-resource</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
              Built for rare and underserved languages.
            </h2>
            <p className="mt-4 text-[15px] text-[#5B6472] leading-relaxed max-w-xl">
              While generic providers focus on the same 12 commercial languages, Petraloc operates a global network of <strong className="text-[#071D49]">native linguists in Caribbean, African, and low-resource ecosystems</strong> — the markets that traditionally get back-translated, machine-translated, or simply ignored.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-y-2.5 gap-x-6">
              {[
                "Haitian Creole",
                "Jamaican Patois",
                "Wolof",
                "Lingala",
                "Yoruba",
                "Twi & Akan",
                "Amharic & Tigrinya",
                "Swahili & Kinyarwanda",
              ].map((l) => (
                <li key={l} className="flex items-center gap-2 text-[14px] text-[#222222]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A55C]" />
                  {l}
                </li>
              ))}
            </ul>
            <Link to="/languages" className="mt-7 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1A4E8A] hover:text-[#00A990] link-ink w-fit">
              Explore all 85+ languages <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Reveal>

          <Reveal variant="left" className="lg:col-span-6">
            <div className="relative rounded-md overflow-hidden border border-[#E5E7EB] bg-[#F7F8FA]">
              <img
                src="https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/yz6uqwoq_Woman%20in%20red%20in%20officeteam%20environment.jpg"
                alt="Native linguist in collaborative office environment"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071D49]/70 via-[#071D49]/15 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <div className="flex items-center gap-2 text-white text-[12px] tracking-[0.18em] uppercase font-semibold">
                  <Globe2 className="w-4 h-4 text-[#00D1B2]" />
                  Native Caribbean network
                </div>
                <span className="text-[12px] tracking-[0.16em] uppercase font-semibold text-[#C9A55C]">
                  85+ markets
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ============ TRUSTED PARTNERS ============ */}
      <PartnersStrip />

      {/* ============ INDUSTRIES (compact tiles) ============ */}
      <Section>
        <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
          <Reveal>
            <p className="eyebrow">Industries</p>
            <h2 className="mt-3 text-3xl lg:text-[36px] font-semibold tracking-tight text-[#071D49] leading-[1.15]">
              Domain expertise where it matters.
            </h2>
          </Reveal>
          <Link to="/industries" className="text-[13px] font-semibold text-[#1A4E8A] hover:text-[#00A990] inline-flex items-center gap-1.5 link-ink">
            All industries <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INDUSTRIES.map((ind, i) => (
            <Reveal variant="up" delay={(i % 3) + 1} key={ind.slug}>
              <Link to={`/industries/${ind.slug}`} data-testid={`home-industry-${ind.slug}`} className="ent-card overflow-hidden group flex flex-col h-full">
                <div className="img-zoom h-40 bg-[#F7F8FA]">
                  <img src={ind.image} alt={ind.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h3 className="text-[15px] font-semibold text-[#071D49] group-hover:text-[#1A4E8A] transition-colors">{ind.title}</h3>
                  <p className="mt-1 text-[12.5px] text-[#5B6472] leading-snug">{ind.short}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ============ ACCURACY ECOSYSTEM ============ */}
      <Section gray>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <Reveal variant="right" className="lg:col-span-5">
            <p className="eyebrow-teal">Accuracy ecosystem</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
              Five disciplines orbiting your content.
            </h2>
            <p className="mt-4 text-[15px] text-[#5B6472] leading-relaxed max-w-md">
              Every Petraloc deliverable is shaped by five forces working in concert — human, cultural, technical, terminological, and AI-augmented.
            </p>
            <ul className="mt-6 space-y-2.5 text-[14px] text-[#222222]">
              {[
                { i: Users, t: "Native fluency", c: "#1A4E8A" },
                { i: Sparkles, t: "Cultural nuance", c: "#C9A55C" },
                { i: Layers, t: "Terminology precision", c: "#1A4E8A" },
                { i: ShieldCheck, t: "Human QA review", c: "#00D1B2" },
                { i: Cpu, t: "AI enhancement", c: "#00D1B2" },
              ].map((it) => (
                <li key={it.t} className="flex items-center gap-3">
                  <it.i className="w-4 h-4" style={{ color: it.c }} />
                  <span className="font-medium">{it.t}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal variant="scale" className="lg:col-span-7">
            <AccuracyEcosystem />
          </Reveal>
        </div>
      </Section>

      {/* ============ COMPARISON ============ */}
      <Section dark>
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <p className="eyebrow-teal">Why clients choose Petraloc</p>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-white leading-[1.1]">
            The difference between a vendor and a partner.
          </h2>
        </Reveal>

        <Reveal variant="up" className="max-w-4xl mx-auto rounded-md border border-white/10 overflow-hidden">
          <div className="grid grid-cols-3 bg-white/5 text-[11px] uppercase tracking-[0.18em] font-semibold">
            <div className="px-5 py-4 text-white/55"> </div>
            <div className="px-5 py-4 text-white/55 border-l border-white/10">Generic Provider</div>
            <div className="px-5 py-4 text-[#00D1B2] border-l border-white/10">Petraloc</div>
          </div>
          {COMPARISON.map((row, i) => (
            <div
              key={row.label}
              data-testid={`comparison-row-${i}`}
              className={`grid grid-cols-3 ${i % 2 === 0 ? "bg-white/[0.02]" : ""}`}
            >
              <div className="px-5 py-4 text-[13px] text-white font-semibold border-t border-white/10">{row.label}</div>
              <div className="px-5 py-4 text-[13px] text-white/60 border-t border-l border-white/10 flex items-start gap-2">
                <X className="w-3.5 h-3.5 mt-1 text-white/30 flex-shrink-0" />
                <span>{row.generic}</span>
              </div>
              <div className="px-5 py-4 text-[13px] text-white border-t border-l border-white/10 flex items-start gap-2">
                <Check className="w-3.5 h-3.5 mt-1 text-[#00D1B2] flex-shrink-0" />
                <span>{row.petraloc}</span>
              </div>
            </div>
          ))}
        </Reveal>
      </Section>

      {/* ============ TESTIMONIALS CAROUSEL ============ */}
      <Section gray>
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <p className="eyebrow">Client voice</p>
          <h2 className="mt-3 text-3xl lg:text-[36px] font-semibold tracking-tight text-[#071D49] leading-[1.15]">
            Trusted by teams that can't afford to be misunderstood.
          </h2>
        </Reveal>
        <TestimonialCarousel />
      </Section>
    </div>
  );
}
